import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  CircleUser,
  ClipboardList,
  Eye,
  Gauge,
  LayoutDashboard,
  LogOut,
  Menu,
  QrCode,
  Receipt,
  Search,
  Settings,
  ShoppingCart,
  Undo2,
  Wallet,
} from 'lucide-react'
import { useAuth, roleHome } from '@/context/AuthContext'
import { useCart } from '@/context/CartContext'
import { cn } from '@/lib/cn'
import Avatar from '@/components/ui/Avatar'
import SearchOverlay from '@/components/storefront/SearchOverlay'
import MobileMenu from '@/components/storefront/MobileMenu'
import { consoleFor } from '@/lib/roles'

/**
 * Public marketplace shell - visitors configure products and build a cart
 * without an account; the login gate only appears at checkout. Customer area
 * lives at /account, staff/admin consoles keep their own layouts.
 */
export default function StorefrontLayout() {
  const { user, logout, impersonating, endImpersonation } = useAuth()
  const { items } = useCart()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const [accountOpen, setAccountOpen] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)

  // Close the account menu on outside click or Escape - same mechanics as the
  // portal top bar's avatar menu.
  useEffect(() => {
    if (!accountOpen) return
    const onMouseDown = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [accountOpen])

  const onLogout = async () => {
    setAccountOpen(false)
    if (impersonating) {
      // Signing out of a "signed in as" session means going back to being
      // the admin; the context lands on admin > Customers itself, so the
      // trip to /login below must not run.
      await endImpersonation()
      return
    }
    await logout()
    navigate('/login')
  }

  const closeSearch = () => {
    setSearchOpen(false)
    // Hand focus back to the trigger so keyboard users are not dropped at body.
    searchButtonRef.current?.focus()
  }

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    menuButtonRef.current?.focus()
  }, [])

  // Plain text links beside the logo, no pill - the reference nav's look.
  const navLink = ({ isActive }: { isActive: boolean }) =>
    cn(
      'px-2.5 py-2 text-sm font-medium transition-colors sm:px-3',
      isActive ? 'text-primary' : 'text-foreground/70 hover:text-foreground',
    )

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Solid on phones: a sticky backdrop-blur re-blurs everything under it
          on every scroll frame, which is what made the home page stutter. At
          95% opacity the blur was barely visible anyway. */}
      <header className="sticky top-0 z-40 border-b border-foreground/10 bg-background sm:bg-background/95 sm:backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4 sm:px-5 sm:h-[70px]">
          <Link to="/" className="flex shrink-0 items-center">
            <img src="/logo_hd.png" alt="MPG Printing" className="h-6 w-auto object-contain sm:h-7" />
          </Link>

          {/* Links live beside the logo, past a thin divider - reference
              layout. Below sm they move into the full-screen menu, because the
              logo, both links and the icon row together overflow a 375px bar. */}
          <span className="hidden h-6 w-px bg-foreground/15 sm:block" />
          <nav className="hidden items-center gap-1 sm:flex">
            <NavLink to="/" end className={navLink}>
              Home
            </NavLink>
            <NavLink to="/products" className={navLink}>
              Products
            </NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
            <button
              type="button"
              ref={searchButtonRef}
              onClick={() => setSearchOpen(true)}
              title="Search"
              aria-label="Search products"
              className="flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-foreground/5 hover:text-foreground"
            >
              <Search className="size-5" />
            </button>
            <Link
              to="/cart"
              title="Cart"
              className="relative flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-foreground/5 hover:text-foreground"
            >
              <ShoppingCart className="size-5" />
              {items.length > 0 && (
                <span className="absolute right-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                  {items.length}
                </span>
              )}
            </Link>
            {!user && (
              <Link
                to="/login"
                title="Sign in or create account"
                aria-label="Sign in or create account"
                className="hidden size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-foreground/5 hover:text-foreground sm:flex"
              >
                <CircleUser className="size-5" />
              </Link>
            )}
            {user?.role === 'customer' && (
              <div ref={accountRef} className="relative hidden sm:block">
                <button
                  type="button"
                  title="My account"
                  aria-label="Account menu"
                  aria-expanded={accountOpen}
                  onClick={() => setAccountOpen((open) => !open)}
                  className="flex size-9 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-medium text-white hover:bg-primary/90"
                >
                  <Avatar user={user} className="size-full" />
                </button>
                {accountOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-foreground/10 bg-background p-1 shadow-xl">
                    <div className="flex items-center gap-2.5 px-3 py-2">
                      <Avatar user={user} className="size-9 shrink-0 bg-primary text-xs font-medium text-white" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{user.name}</p>
                        <p className="truncate text-xs text-foreground/60">{user.email}</p>
                      </div>
                    </div>
                    <div className="mx-1 h-px bg-foreground/10" />
                    <Link
                      to="/account"
                      onClick={() => setAccountOpen(false)}
                      className="mt-1 flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-foreground/5"
                    >
                      <LayoutDashboard className="size-4 stroke-[1.5]" />
                      My Account
                    </Link>
                    <Link
                      to="/account/orders"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-foreground/5"
                    >
                      <ClipboardList className="size-4 stroke-[1.5]" />
                      My Orders
                    </Link>
                    <Link
                      to="/account/wallet"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-foreground/5"
                    >
                      <Wallet className="size-4 stroke-[1.5]" />
                      Wallet
                    </Link>
                    <Link
                      to="/account/settings"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-foreground/5"
                    >
                      <Settings className="size-4 stroke-[1.5]" />
                      Settings
                    </Link>
                    <button
                      type="button"
                      onClick={onLogout}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-foreground/5"
                    >
                      {impersonating ? (
                        <>
                          <Undo2 className="size-4 stroke-[1.5]" />
                          Return to admin console
                        </>
                      ) : (
                        <>
                          <LogOut className="size-4 stroke-[1.5]" />
                          Sign out
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
            {user && user.role !== 'customer' && (
              // Below sm this lives in the menu instead, where it can carry its
              // label rather than being reduced to a bare icon.
              <Link
                to={roleHome(user)}
                className="hidden h-10 items-center gap-2 rounded-full border border-foreground/15 px-4 text-sm font-semibold text-foreground hover:bg-foreground/5 sm:inline-flex"
              >
                {consoleFor(user).icon === 'gauge' ? (
                  <Gauge className="size-4" />
                ) : consoleFor(user).icon === 'receipt' ? (
                  <Receipt className="size-4" />
                ) : (
                  <QrCode className="size-4" />
                )}
                {consoleFor(user).label}
              </Link>
            )}

            {/* Everything hidden above is reachable here below sm. */}
            <button
              type="button"
              ref={menuButtonRef}
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              className="flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-foreground/5 hover:text-foreground sm:hidden"
            >
              <Menu className="size-5" />
            </button>
          </div>
        </div>

        {impersonating && user && (
          // An admin "signed in as" this customer. Part of the sticky header
          // so it stays on screen for the whole session and can never be
          // mistaken for the real thing. A dark band, the storefront's own
          // recipe - the tinted portal chip is banned out here. Stacks on
          // phones.
          <div className="bg-night text-white">
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:px-5">
              <p className="flex min-w-0 flex-1 items-start gap-2 text-white/70">
                <Eye className="mt-0.5 size-4 shrink-0 stroke-[1.5]" />
                <span>
                  <span className="font-semibold text-white">Signed in as {user.name}</span> (customer account). You
                  are seeing exactly what they see.
                </span>
              </p>
              <button
                type="button"
                onClick={() => endImpersonation()}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-white/30 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-white/10"
              >
                <Undo2 className="size-3.5" />
                Return to admin console
              </button>
            </div>
          </div>
        )}
      </header>

      <SearchOverlay open={searchOpen} onClose={closeSearch} />
      <MobileMenu open={menuOpen} onClose={closeMenu} />

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-night">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-5 py-10 sm:grid-cols-3">
          <div>
            <div className="flex items-center">
              <Link to="/" title="MPG marketplace">
                <img src="/logo_hd_white.png" alt="MPG Printing" className="h-5 w-auto object-contain" />
              </Link>
            </div>
            <p className="mt-3 max-w-xs text-sm text-white/60">
              Custom apparel printing for teams and businesses - jerseys, corporate wear, caps and more, produced on a
              tracked five-stage line.
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-white/60">Shop</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link to="/products" className="text-white/70 hover:text-white">
                  All products
                </Link>
              </li>
              <li>
                <Link to="/products?category=Jersey" className="text-white/70 hover:text-white">
                  Jerseys
                </Link>
              </li>
              <li>
                <Link to="/products?category=T-Shirt" className="text-white/70 hover:text-white">
                  T-Shirts
                </Link>
              </li>
              <li>
                <Link to="/products?category=Jacket" className="text-white/70 hover:text-white">
                  Jackets
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-white/60">Account</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link to="/register" className="text-white/70 hover:text-white">
                  Create free account
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-white/70 hover:text-white">
                  Sign in
                </Link>
              </li>
              <li>
                <Link to="/account/wallet" className="text-white/70 hover:text-white">
                  Wallet and price tiers
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/60">
          Copyright {new Date().getFullYear()} MPG Printing. All rights reserved.
        </div>
      </footer>
    </div>
  )
}
